import { EASY_READ_JOURNEYS } from '../data/easyReadJourneys.js';
import { EASY_READ_SHARED_COPY } from '../data/easyReadSharedCopy.js';
import { WAITING_FOR_SURGERY_TEMPLATE, WAITING_FOR_SURGERY_SECTIONS, WAITING_FOR_SURGERY_LINKS } from '../data/waitingForSurgeryJourney.js';
import { WAITING_SUPPORT_COPY, WAITING_SUPPORT_CHOICES } from '../data/waitingForSurgerySupport.js';
import { WAITING_NATIONAL_RESOURCES, WAITING_NATIONAL_RESOURCE_INTRO } from '../data/waitingForSurgeryResources.js';

// These are patient-visible data-driven English strings that are NOT yet
// served from locale resources. Do not describe a language as fully covered
// while a source remains in this list. A later content migration must move
// these to published locales before marking them reviewable/publishable.
const EXCLUSION_REASON = 'Deliberately English-only for the current release: Easy Read and Waiting for Surgery are not included in the professional multilingual review. Preserve this exclusion until the product owner explicitly enables translation of these features.';

function rowsFor(value, prefix, rows) {
  if (typeof value === 'string') {
    if (value.trim()) rows.push({
      id: 'external:' + prefix, namespace: 'external', path: prefix,
      contentArea: 'hospital_journeys', sourceText: value, originalText: '',
      publicationTarget: 'excluded', exclusionCode: 'intentionally_english_only', exclusionReason: EXCLUSION_REASON,
    });
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((part, i) => rowsFor(part, prefix + '.' + i, rows));
    return;
  }
  if (value && typeof value === 'object') {
    Object.entries(value).forEach(([key, part]) => rowsFor(part, prefix + '.' + key, rows));
  }
}

function copyFields(item, fields) {
  return Object.fromEntries(fields.filter((field) => typeof item?.[field] === 'string'
    || Array.isArray(item?.[field])).map((field) => [field, item[field]]));
}

export function buildUnintegratedPatientCopyRows() {
  const rows = [];
  rowsFor(copyFields(WAITING_FOR_SURGERY_TEMPLATE, ['title', 'summary']), 'waitingForSurgery.template', rows);
  rowsFor(WAITING_FOR_SURGERY_SECTIONS.map((stage) => copyFields(stage,
    ['title', 'description', 'body'])), 'waitingForSurgery.sections', rows);
  rowsFor(WAITING_FOR_SURGERY_LINKS.map((link) => copyFields(link,
    ['title', 'description'])), 'waitingForSurgery.links', rows);
  rowsFor(WAITING_SUPPORT_CHOICES.map((choice) => copyFields(choice,
    ['label', 'description'])), 'waitingForSurgery.choices', rows);
  rowsFor(WAITING_SUPPORT_COPY, 'waitingForSurgery.support', rows);
  rowsFor(WAITING_NATIONAL_RESOURCE_INTRO, 'waitingForSurgery.resourcesIntro', rows);
  rowsFor(WAITING_NATIONAL_RESOURCES.map((resource) => copyFields(resource,
    ['title', 'description'])), 'waitingForSurgery.nationalResources', rows);

  rowsFor(EASY_READ_SHARED_COPY, 'easyRead.shared', rows);

  for (const [id, journey] of Object.entries(EASY_READ_JOURNEYS)) {
    const prefix = 'easyRead.' + id;
    rowsFor(copyFields(journey, ['title', 'eyebrow', 'introduction', 'disclaimer']), prefix + '.intro', rows);
    rowsFor(copyFields(journey.exploreMore, ['title', 'description', 'linkLabel']), prefix + '.exploreMore', rows);
    rowsFor((journey.roles || []).map((role) => copyFields(role,
      ['title', 'shortTitle', 'shortDescription'])), prefix + '.roles', rows);
    rowsFor((journey.stages || []).map((stage) => ({
      ...copyFields(stage, ['title', 'description', 'body']),
      image: copyFields(stage.easyReadImage, ['alt', 'label']),
    })), prefix + '.stages', rows);
    rowsFor((journey.usefulLinks || []).map((link) => copyFields(link,
      ['title', 'description'])), prefix + '.links', rows);
    rowsFor(journey.questions || [], prefix + '.questions', rows);
  }
  const seen = new Set();
  return rows.filter((row) => { if (seen.has(row.id)) throw new Error('Duplicate English source: ' + row.id);
    seen.add(row.id); return true; });
}
